import { useId } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Textarea } from '@/components/ui/textarea'

type PackagingManufacturerSectionLabels = {
  question: string
  optionYes: string
  optionNo: string
  manufacturerName: string
  manufacturerAddress: string
  manufacturerNameRequired?: string
  manufacturerAddressRequired?: string
}

type PackagingManufacturerSectionProps = {
  supplierIsManufacturer: boolean | undefined
  onSupplierIsManufacturerChange: (value: boolean) => void
  manufacturerName: string
  onManufacturerNameChange: (value: string) => void
  manufacturerAddress: string
  onManufacturerAddressChange: (value: string) => void
  supplierName: string
  supplierAddress: string
  labels: PackagingManufacturerSectionLabels
  disabled?: boolean
}

export function isPackagingManufacturerSectionValid(
  supplierIsManufacturer: boolean | undefined,
  manufacturerName: string,
  manufacturerAddress: string
) {
  if (supplierIsManufacturer === undefined) {
    return false
  }
  return (
    manufacturerName.trim().length > 0 && manufacturerAddress.trim().length > 0
  )
}

export function buildPackagingManufacturerPayload(
  supplierIsManufacturer: boolean,
  manufacturerName: string,
  manufacturerAddress: string
) {
  return {
    supplierIsManufacturer,
    manufacturerName: manufacturerName.trim(),
    manufacturerAddress: manufacturerAddress.trim(),
  }
}

export function PackagingManufacturerSection({
  supplierIsManufacturer,
  onSupplierIsManufacturerChange,
  manufacturerName,
  onManufacturerNameChange,
  manufacturerAddress,
  onManufacturerAddressChange,
  supplierName,
  supplierAddress,
  labels,
  disabled = false,
}: PackagingManufacturerSectionProps) {
  const radioGroupId = useId()

  let supplierIsManufacturerValue = ''
  if (supplierIsManufacturer === true) {
    supplierIsManufacturerValue = 'true'
  } else if (supplierIsManufacturer === false) {
    supplierIsManufacturerValue = 'false'
  }

  const handleSupplierIsManufacturerChange = (nextValue: string) => {
    if (nextValue === 'true') {
      onSupplierIsManufacturerChange(true)
      onManufacturerNameChange(supplierName.trim())
      onManufacturerAddressChange(supplierAddress.trim())
    }
    if (nextValue === 'false') {
      onSupplierIsManufacturerChange(false)
      onManufacturerNameChange('')
      onManufacturerAddressChange('')
    }
  }

  const showManufacturerNameRequiredError =
    supplierIsManufacturer !== undefined && manufacturerName.trim().length === 0
  const showManufacturerAddressRequiredError =
    supplierIsManufacturer !== undefined &&
    manufacturerAddress.trim().length === 0
  const manufacturerFieldsDisabled =
    disabled || supplierIsManufacturer === true

  return (
    <div className='space-y-4'>
      <div className='space-y-3'>
        <Label>{labels.question}</Label>
        <RadioGroup
          value={supplierIsManufacturerValue}
          onValueChange={handleSupplierIsManufacturerChange}
          disabled={disabled}
          aria-label={labels.question}
        >
          <div className='flex items-center gap-2'>
            <RadioGroupItem
              value='true'
              id={`${radioGroupId}-supplier-is-manufacturer-yes`}
            />
            <Label
              htmlFor={`${radioGroupId}-supplier-is-manufacturer-yes`}
              className='font-normal'
            >
              {labels.optionYes}
            </Label>
          </div>
          <div className='flex items-center gap-2'>
            <RadioGroupItem
              value='false'
              id={`${radioGroupId}-supplier-is-manufacturer-no`}
            />
            <Label
              htmlFor={`${radioGroupId}-supplier-is-manufacturer-no`}
              className='font-normal'
            >
              {labels.optionNo}
            </Label>
          </div>
        </RadioGroup>
      </div>

      <div className='space-y-4'>
        <div className='space-y-1'>
          <Label htmlFor={`${radioGroupId}-manufacturer-name`}>
            {labels.manufacturerName}
          </Label>
          <Input
            id={`${radioGroupId}-manufacturer-name`}
            value={manufacturerName}
            onChange={(event) => {
              onManufacturerNameChange(event.target.value)
            }}
            disabled={manufacturerFieldsDisabled}
          />
          {showManufacturerNameRequiredError &&
            labels.manufacturerNameRequired && (
              <p className='text-sm text-destructive'>
                {labels.manufacturerNameRequired}
              </p>
            )}
        </div>
        <div className='space-y-1'>
          <Label htmlFor={`${radioGroupId}-manufacturer-address`}>
            {labels.manufacturerAddress}
          </Label>
          <Textarea
            id={`${radioGroupId}-manufacturer-address`}
            value={manufacturerAddress}
            onChange={(event) => {
              onManufacturerAddressChange(event.target.value)
            }}
            disabled={manufacturerFieldsDisabled}
            rows={3}
          />
          {showManufacturerAddressRequiredError &&
            labels.manufacturerAddressRequired && (
              <p className='text-sm text-destructive'>
                {labels.manufacturerAddressRequired}
              </p>
            )}
        </div>
      </div>
    </div>
  )
}
